import { CommonModule } from "@angular/common";
import {
  Component,
  effect,
  inject,
  Input,
  OnInit,
  signal,
} from "@angular/core";
import { FormBuilder, ReactiveFormsModule, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { UserInfo } from "@models/user";
import { BlobStorageService } from "@services/blob-storage.service";
import { EventsManagementService } from "@services/events-management.service";
import { LocalStorageService } from "@services/local-storage.service";
import { SnackBarService } from "@services/snack-bar.service";
import { ComboTicketManagementService } from "@services/combo-ticket-management.service";
import { SpaceManagementService } from "@services/space-management.service";
import { EventSpacesSyncService } from "@services/event-spaces-sync.service";
import { EventCombosSyncService } from "@services/event-combos-sync.service";
import { TicketManagementService } from "@services/ticket-management.service";
import { TicketBatchResponseBody } from "@services/types/ticket-batch-response.type";
import { EventInfoRequestBody } from "@services/types/events-management-request.type";
import {
  EventCategory,
  EventInfoResponseBody,
  ICreateEventResponse,
} from "@services/types/events-management-response.type";
import { defaultEventInfoResponseBody } from "@services/utils/events-management-response.default-values";
import { TicketTypeEnum } from "@models/enums/ticket-type.enum";
import { EventComboTicket } from "@models/combo-ticket";
import { EventSpace } from "@models/space";
import { isEmpty } from "lodash-es";
import { DateTime } from "luxon";
import { Stepper, StepperModule } from "primeng/stepper";
import { first, firstValueFrom, Observable } from "rxjs";
import { environment } from "../../../../environments/environment";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { EventTicketBatch } from "../../../../shared/components/modal-tickets/types/ticket-batch.type";
import { normalizeBatches } from "../../../../shared/helpers/batch-transition/normalize-batches.helper";
import {
  buildEventForm,
  mapEventInfoToEventFormValue,
} from "./event-form.form";
import { EventAddressStepComponent } from "./steps/event-address-step/event-address-step.component";
import {
  EventBasicInfoStepComponent,
  MediaSelection,
} from "./steps/event-basic-info-step/event-basic-info-step.component";
import { EventDateStepComponent } from "./steps/event-date-step/event-date-step.component";
import { EventComboStepComponent } from "./steps/event-combo-step/event-combo-step.component";
import { EventSpacesStepComponent } from "./steps/event-spaces-step/event-spaces-step.component";
import { EventSummaryStepComponent } from "./steps/event-summary-step/event-summary-step.component";
import { EventTicketsStepComponent } from "./steps/event-tickets-step/event-tickets-step.component";
import { EventTypeStepComponent } from "./steps/event-type-step/event-type-step.component";
import { describeEventSaveError, EventSaveError } from "./event-save-error";
import { validTicketPrice } from "../../../../shared/helpers/payment-minimum.helper";

type ActivateCallback = (value: number) => void;

@Component({
  selector: "app-event-form",
  imports: [
    // Angular
    CommonModule,
    ReactiveFormsModule,
    // Prime Ng
    StepperModule,
    Stepper,
    // Componts
    ButtonComponent,
    EventBasicInfoStepComponent,
    EventDateStepComponent,
    EventAddressStepComponent,
    EventTypeStepComponent,
    EventTicketsStepComponent,
    EventComboStepComponent,
    EventSpacesStepComponent,
    EventSummaryStepComponent,
  ],
  templateUrl: "./event-form.component.html",
  styleUrl: "./event-form.component.scss",
})
export class EventFormComponent implements OnInit {
  @Input() eventId?: string;

  private fb = inject(FormBuilder);
  private readonly eventsManagementService = inject(EventsManagementService);
  private readonly router = inject(Router);
  private readonly ticketsManagementService = inject(TicketManagementService);
  private readonly spaceManagementService = inject(SpaceManagementService);
  private readonly comboTicketManagementService = inject(
    ComboTicketManagementService,
  );
  private readonly spacesSync = inject(EventSpacesSyncService);
  private readonly combosSync = inject(EventCombosSyncService);
  private readonly blobService = inject(BlobStorageService);
  private readonly snackbarService = inject(SnackBarService);
  private readonly localStorageService = inject(LocalStorageService);

  private sasToken = environment.SAS_TOKEN_BLOB_STORAGE_AZURE;
  form = buildEventForm(this.fb);

  currentUser: UserInfo = JSON.parse(
    this.localStorageService.getItem("current_user"),
  );

  eventCategories$?: Observable<EventCategory[] | null>;

  currentStep = 0;

  steps = [
    "basicInfo",
    "date",
    "address",
    "type",
    "tickets",
    "combos",
    "spaces",
    "summary",
  ];
  formGroups = this.form.controls;

  private localBannerFile: File | null = null;
  private localMapFile: File | null = null;

  private bannerBlobPath = "";
  private mapBlobPath = "";

  eventInfo: EventInfoResponseBody = defaultEventInfoResponseBody;

  initialBannerPreview: string | null = null;
  initialMapPreview: string | null = null;

  readonly ticketBatchesSignal = signal<EventTicketBatch[]>([]);
  readonly spacesSignal = signal<EventSpace[]>([]);
  readonly combosSignal = signal<EventComboTicket[]>([]);
  readonly isSubmitting = signal(false);
  readonly saveError = signal<EventSaveError | null>(null);
  readonly invalidSteps = signal<Set<number>>(new Set());
  private hasAttemptedSubmit = false;
  private originalTicketBatches: EventTicketBatch[] = [];
  private originalSpaces: EventSpace[] = [];
  private originalCombos: EventComboTicket[] = [];

  get currentStepKey() {
    return this.steps[this.currentStep];
  }

  get isFreeEvent(): boolean {
    return !this.formGroups.eventType.controls.isEventPaid.value;
  }

  get isPrivateEvent(): boolean {
    return this.formGroups.eventSummary.controls.isPrivate.value;
  }

  /** Gratuito-privado: ingressos vêm da lista de convidados (pula passos 5, 6 e 7). */
  get isFreePrivate(): boolean {
    return this.isFreeEvent && this.isPrivateEvent;
  }

  /** Gratuito-público: 1 ingresso R$ 0 (pula combo e espaços). */
  get isFreePublic(): boolean {
    return this.isFreeEvent && !this.isPrivateEvent;
  }

  constructor() {
    // Recalcula os steps inválidos quando o usuário corrige batches/espaços
    // (só depois da primeira tentativa de submit)
    effect(() => {
      this.ticketBatchesSignal();
      this.spacesSignal();
      if (this.hasAttemptedSubmit) {
        this.recomputeInvalidSteps();
      }
    });
  }

  ngOnInit(): void {
    const mappedEventValue = mapEventInfoToEventFormValue(this.eventInfo);
    this.form.patchValue(mappedEventValue);
    this.loadEventCategories();
    this.getEventInfo();
    this.getEventTicketBatches();
    this.getEventSpaces();
    this.getEventCombos();

    // Recalcula invalidSteps sempre que qualquer form group muda de status
    // (só tem efeito depois da primeira tentativa de submit)
    this.form.statusChanges.subscribe(() => {
      if (this.hasAttemptedSubmit) {
        this.recomputeInvalidSteps();
      }
    });
  }

  private recomputeInvalidSteps(): void {
    this.collectInvalidSteps(
      this.validatedTicketBatchesSilent(),
      this.validateBatchesDateRangesSilent(),
      this.validateSpacesDateRangesSilent(),
    );
  }

  private validatedTicketBatchesSilent(): boolean {
    const ticketBatches = this.ticketBatchesSignal();
    if (isEmpty(ticketBatches)) return false;
    return ticketBatches.every(
      (batch) => Array.isArray(batch.tickets) && batch.tickets.length > 0 &&
        batch.tickets.every((ticket) => validTicketPrice(Number(ticket.price))),
    );
  }

  private validateBatchesDateRangesSilent(): boolean {
    const now = DateTime.local();
    return !this.ticketBatchesSignal().some((batch) => {
      if (this.eventId && batch.id) return false;
      const saleStart = DateTime.fromISO(String(batch.sale_start));
      const saleEnd = DateTime.fromISO(String(batch.sale_end));
      return (
        (saleStart.isValid && saleStart < now) ||
        (saleEnd.isValid && saleEnd < now)
      );
    });
  }

  private validateSpacesDateRangesSilent(): boolean {
    const now = DateTime.local();
    return !this.spacesSignal().some((space) => {
      if (this.eventId && space.id) return false;
      const saleStart = DateTime.fromISO(String(space.sale_start));
      const saleEnd = DateTime.fromISO(String(space.sale_end));
      return (
        (saleStart.isValid && saleStart < now) ||
        (saleEnd.isValid && saleEnd < now)
      );
    });
  }

  onMediaSelected(e: MediaSelection) {
    if (e.control === "uploadBanner") {
      this.localBannerFile = e.file;
      this.bannerBlobPath = e.blobPath;
    } else {
      this.localMapFile = e.file;
      this.mapBlobPath = e.blobPath;
    }
  }

  onMediaCleared(control: "uploadBanner" | "mapaEvento") {
    if (control === "uploadBanner") {
      this.localBannerFile = null;
      this.bannerBlobPath = "";
    } else {
      this.localMapFile = null;
      this.mapBlobPath = "";
    }
  }

  onNext(currentStepValue: number, activateCallback: ActivateCallback): void {
    const stepIndex = currentStepValue - 1;
    if (!this.validateStep(stepIndex)) {
      return;
    }
    activateCallback(this.nextStepValue(currentStepValue));
  }

  /** Próximo passo considerando os saltos do fluxo gratuito. */
  private nextStepValue(currentStepValue: number): number {
    // Tipo/visibilidade (4): gratuito-privado vai direto à revisão (8).
    if (currentStepValue === 4 && this.isFreePrivate) {
      return 8;
    }
    // Ingressos (5): qualquer gratuito pula combo (6) e espaços (7) e vai à revisão (8).
    if (currentStepValue === 5 && this.isFreeEvent) {
      return 8;
    }
    return currentStepValue + 1;
  }

  /** Passo anterior considerando os saltos do fluxo gratuito. */
  prevStepValue(currentStepValue: number): number {
    if (currentStepValue === 8) {
      if (this.isFreePrivate) return 4;
      if (this.isFreePublic) return 5;
      return 7;
    }
    return currentStepValue - 1;
  }

  onTicketBatchesChange(updatedTicketBatches: EventTicketBatch[]): void {
    this.ticketBatchesSignal.set(updatedTicketBatches);
  }

  onBatchMetadataSaved(savedBatches: TicketBatchResponseBody[]): void {
    this.originalTicketBatches = this.originalTicketBatches.map((original) => {
      const saved = savedBatches.find((batch) => batch.id === original.id);
      if (!saved) return original;
      const { tickets, ...metadata } = saved;
      const confirmedTickets = original.tickets.map((ticket) => ({ ...ticket }));
      // Half-price stock generated by the batch save is confirmed; local edits
      // to existing ticket prices/details still remain pending independently.
      for (const ticket of tickets ?? []) {
        if (ticket.half_price && !confirmedTickets.some((item) => item.id === ticket.id)) {
          confirmedTickets.push({ ...ticket, price: Number(ticket.price) } as EventTicketBatch["tickets"][number]);
        }
      }
      return { ...original, ...metadata, tickets: confirmedTickets };
    });
  }

  hasUnsavedBatchChanges(): boolean {
    return (
      JSON.stringify(this.ticketBatchesSignal()) !==
      JSON.stringify(this.originalTicketBatches)
    );
  }

  onBatchTransitioned(updatedTicketBatches: EventTicketBatch[]): void {
    if (!this.eventId) return;
    const batches = normalizeBatches(
      updatedTicketBatches.map((batch) => ({
        ...batch,
        tickets: batch.tickets.filter(
          (ticket) =>
            ticket.type !== TicketTypeEnum.SpaceTicket &&
            ticket.type !== TicketTypeEnum.ComboTicket,
        ),
      })),
    ).filter((batch) => batch.tickets.length > 0);
    this.ticketBatchesSignal.set(batches);
    this.originalTicketBatches = JSON.parse(JSON.stringify(batches));
  }

  onSpacesChange(updatedSpaces: EventSpace[]): void {
    this.spacesSignal.set(updatedSpaces);
  }

  onCombosChange(updatedCombos: EventComboTicket[]): void {
    this.combosSignal.set(updatedCombos);
  }

  async submit() {
    if (this.isSubmitting()) return;
    this.saveError.set(null);

    this.form.markAllAsTouched();

    this.hydrateMediaFromFormIfNeeded();

    // força re-execução dos validators cross-field que dependem de "agora"
    // (datas podem ter ficado no passado enquanto o usuário preenchia o form)
    this.revalidateTimeSensitiveControls();

    // valida step final (summary) + lotes + espaços
    const isTicketsOk = this.validatedTicketBatches();
    const areBatchesDateRangesOk = this.validateBatchesDateRanges();
    const areSpacesDateRangesOk = this.validateSpacesDateRanges();
    const isFormValid =
      this.form.valid &&
      isTicketsOk &&
      areBatchesDateRangesOk &&
      areSpacesDateRangesOk;

    this.hasAttemptedSubmit = true;
    this.collectInvalidSteps(
      isTicketsOk,
      areBatchesDateRangesOk,
      areSpacesDateRangesOk,
    );

    if (!isFormValid) {
      const stepLabels: Record<number, string> = {
        1: "Informações básicas: confira nome, categoria, descrição e banner.",
        2: "Data e horário: confira o início e o término do evento.",
        3: "Endereço: confira os campos obrigatórios do local.",
        4: "Tipo e visibilidade: confira os dados do evento.",
        5: "Ingressos: confira os lotes, as datas de venda e os ingressos cadastrados.",
        7: "Espaços: confira as datas de venda dos espaços cadastrados.",
        8: "Revisão: confira os campos obrigatórios desta etapa.",
      };
      const details = [...this.invalidSteps()].map((step) => stepLabels[step]);
      if (this.formGroups.basicInfo.controls.eventCategoryId.invalid)
        details.unshift(
          "Selecione uma categoria válida para o evento na etapa Informações básicas.",
        );
      this.saveError.set({ title: "Revise os dados antes de salvar.", details });
      this.snackbarService.openSnackBar(
        `Revise os dados antes de salvar. ${details.join(" ")}`,
        "Fechar",
        "error",
      );
      return;
    }

    this.isSubmitting.set(true);

    try {
      const thumbnail = await this.uploadBannerIfNeeded();
      const additionalImages = await this.uploadMapIfNeeded();

      console.log(this.form.value);
      console.log(thumbnail);
      console.log(additionalImages);
      if (this.eventId) {
        await this.submitUpdateEvent(thumbnail, additionalImages);
      } else {
        await this.submitCreateEvent(thumbnail, additionalImages);
      }
    } catch (error) {
      this.isSubmitting.set(false);
      console.error("Error uploading images:", error);
      this.snackbarService.openSnackBar(
        "Erro ao fazer upload das imagens. Tente novamente.",
        "Fechar",
        "error",
      );
    }
  }

  private getEventInfo() {
    if (!this.eventId) {
      return;
    }

    this.eventsManagementService
      .getMyEventById(Number(this.eventId))
      .subscribe((response) => {
        this.eventInfo = response;
        const mappedEventValue = mapEventInfoToEventFormValue(this.eventInfo);
        this.form.patchValue(mappedEventValue);
        this.syncInitialMediaPreviewsFromEventInfo();
        this.form.markAsPristine();
      });
  }

  private getEventTicketBatches() {
    if (!this.eventId) {
      return;
    }

    this.eventsManagementService
      .getEventBatches(Number(this.eventId))
      .subscribe((response) => {
        // Remove SpaceTicket e ComboTicket — gerenciados nas etapas de espaço e combo.
        // Lotes que ficarem sem tickets após a filtragem também são descartados.
        const filtered = (response ?? [])
          .map((batch) => ({
            ...batch,
            tickets: (batch.tickets ?? []).filter(
              (ticket) =>
                ticket.type !== TicketTypeEnum.SpaceTicket &&
                ticket.type !== TicketTypeEnum.ComboTicket,
            ),
          }))
          .filter((batch) => batch.tickets.length > 0);

        this.ticketBatchesSignal.set(filtered);
        this.originalTicketBatches = JSON.parse(JSON.stringify(filtered));
      });
  }

  private getEventSpaces() {
    if (!this.eventId) {
      return;
    }

    this.spaceManagementService
      .getSpaces(Number(this.eventId))
      .subscribe((response) => {
        this.spacesSignal.set(response ?? []);
        this.originalSpaces = JSON.parse(JSON.stringify(response));
      });
  }

  private getEventCombos() {
    if (!this.eventId) {
      return;
    }

    this.comboTicketManagementService
      .getComboTickets(Number(this.eventId))
      .subscribe((response) => {
        this.combosSignal.set(response ?? []);
        this.originalCombos = JSON.parse(JSON.stringify(response));
      });
  }

  private loadEventCategories(): void {
    this.eventCategories$ = this.eventsManagementService.getEventsCategories();
  }

  private validateStep(stepIndex: number): boolean {
    const bannerFromForm =
      this.formGroups.basicInfo.controls.media.controls.uploadBanner.value;

    console.log("[debug banner]", {
      bannerFromForm,
      isBannerFile: bannerFromForm instanceof File,
      localBannerFile: this.localBannerFile,
      bannerBlobPath: this.bannerBlobPath,
      eventInfoThumbnail: this.eventInfo.thumbnail,
    });

    switch (stepIndex) {
      case 0:
        this.formGroups.basicInfo.markAllAsTouched();
        return this.formGroups.basicInfo.valid;

      case 1:
        this.formGroups.eventDates.markAllAsTouched();
        this.formGroups.eventDates.updateValueAndValidity();
        return this.formGroups.eventDates.valid;

      case 2:
        this.formGroups.address.markAllAsTouched();
        return this.formGroups.address.valid;

      case 3:
        this.formGroups.eventType.markAllAsTouched();
        return this.formGroups.eventType.valid;

      case 4:
        return (
          this.validatedTicketBatches() && this.validateBatchesDateRanges()
        );

      case 5:
        // Combo step — optional
        return true;

      case 6:
        // Spaces step — optional
        return true;

      case 7:
        this.formGroups.eventSummary.markAllAsTouched();
        return this.formGroups.eventSummary.valid;

      default:
        return true;
    }
  }

  private async uploadMapIfNeeded(): Promise<string[]> {
    let additionalImages = this.eventInfo.additional_images ?? [];

    if (this.localMapFile && this.mapBlobPath) {
      await this.blobService.uploadImageAsync(
        this.sasToken,
        this.localMapFile,
        this.mapBlobPath,
      );
      additionalImages = [this.mapBlobPath];
    }

    return additionalImages;
  }

  private async uploadBannerIfNeeded(): Promise<string> {
    // se não selecionou novo, mantém o thumbnail atual (ou vazio)
    let imageUrl = this.eventInfo.thumbnail ?? "";

    if (this.localBannerFile && this.bannerBlobPath) {
      await this.blobService.uploadImageAsync(
        this.sasToken,
        this.localBannerFile,
        this.bannerBlobPath,
      );
      imageUrl = this.bannerBlobPath;
    }

    return imageUrl;
  }

  private revalidateTimeSensitiveControls(): void {
    this.formGroups.eventDates.updateValueAndValidity();
  }

  private collectInvalidSteps(
    isTicketsOk: boolean,
    areBatchesDateRangesOk: boolean,
    areSpacesDateRangesOk: boolean,
  ): void {
    const invalid = new Set<number>();

    if (this.formGroups.basicInfo.invalid) invalid.add(1);
    if (this.formGroups.eventDates.invalid) invalid.add(2);
    if (this.formGroups.address.invalid) invalid.add(3);
    if (this.formGroups.eventType.invalid) invalid.add(4);
    if (!this.isFreePrivate && (!isTicketsOk || !areBatchesDateRangesOk)) {
      invalid.add(5);
    }
    if (!this.isFreeEvent && !areSpacesDateRangesOk) invalid.add(7);
    if (this.formGroups.eventSummary.invalid) invalid.add(8);

    this.invalidSteps.set(invalid);
  }

  isStepInvalid(step: number): boolean {
    return this.invalidSteps().has(step);
  }

  private validateBatchesDateRanges(): boolean {
    // Valida que sale_start/sale_end não estão no passado.
    // Em edit mode, pula lotes já persistidos (com id) — só valida novos.
    const now = DateTime.local();
    const batches = this.ticketBatchesSignal();

    const hasPastDate = batches.some((batch) => {
      if (this.eventId && batch.id) return false;
      const saleStart = DateTime.fromISO(String(batch.sale_start));
      const saleEnd = DateTime.fromISO(String(batch.sale_end));
      return (
        (saleStart.isValid && saleStart < now) ||
        (saleEnd.isValid && saleEnd < now)
      );
    });

    if (hasPastDate) {
      this.snackbarService.openSnackBar(
        "Há lotes com datas de venda no passado. Edite os lotes antes de salvar.",
        "Fechar",
        "error",
      );
      return false;
    }

    return true;
  }

  private validateSpacesDateRanges(): boolean {
    // Valida que sale_start/sale_end de espaços não estão no passado.
    // Em edit mode, pula espaços já persistidos (com id) — só valida novos.
    const now = DateTime.local();
    const spaces = this.spacesSignal();

    const hasPastDate = spaces.some((space) => {
      if (this.eventId && space.id) return false;
      const saleStart = DateTime.fromISO(String(space.sale_start));
      const saleEnd = DateTime.fromISO(String(space.sale_end));
      return (
        (saleStart.isValid && saleStart < now) ||
        (saleEnd.isValid && saleEnd < now)
      );
    });

    if (hasPastDate) {
      this.snackbarService.openSnackBar(
        "Há espaços com datas de venda no passado. Edite os espaços antes de salvar.",
        "Fechar",
        "error",
      );
      return false;
    }

    return true;
  }

  private validatedTicketBatches(): boolean {
    // Gratuito-privado não cria ingressos no wizard (vêm da lista de convidados).
    if (this.isFreePrivate) {
      return true;
    }

    const ticketBatches = this.ticketBatchesSignal();

    const hasNoBatches = isEmpty(ticketBatches);

    if (hasNoBatches) {
      this.snackbarService.openSnackBar(
        this.isFreeEvent
          ? "Necessário criar o ingresso gratuito do evento."
          : "Necessário criar pelo menos um lote.",
        "Fechar",
        "error",
      );
      return false;
    }

    const allBatchesHaveAtLeastOneTicket = ticketBatches.every(
      (ticketBatch) =>
        Array.isArray(ticketBatch.tickets) && ticketBatch.tickets.length > 0,
    );

    if (!allBatchesHaveAtLeastOneTicket) {
      this.snackbarService.openSnackBar(
        "Necessário que os lotes possuam pelo menos um tipo de ingresso",
        "Fechar",
        "error",
      );
    }

    if (!allBatchesHaveAtLeastOneTicket) return false;
    if (ticketBatches.some((batch) => batch.tickets.some((ticket) => !validTicketPrice(Number(ticket.price))))) {
      this.snackbarService.openSnackBar(
        "O preço dos ingressos deve ser zero (gratuito) ou de pelo menos R$ 1,00, inclusive na meia-entrada.",
        "Fechar", "error",
      );
      return false;
    }
    return true;
  }

  private async submitCreateEvent(
    thumbnail: string,
    additionalImages: string[],
  ): Promise<void> {
    const requestBody = this.buildEventRequestBody(thumbnail, additionalImages);
    let response: ICreateEventResponse;
    try {
      response = await firstValueFrom(
        this.eventsManagementService.createEvent(requestBody),
      );
    } catch (error) {
      this.showSaveError(error, true);
      return;
    }

    // Remember the parent immediately: a partial failure must not create another event.
    this.eventId = String(response.event_id);
    this.rememberSavedEvent(requestBody.event);
    await this.finishSavingEvent(true);
  }

  private eventDestinationRoute(eventId?: number | string): string {
    if (!eventId) return "/my-events";
    return this.isFreePrivate
      ? `/producer-area/event/${eventId}`
      : `/event/${eventId}`;
  }

  private async submitUpdateEvent(
    thumbnail: string,
    additionalImages: string[],
  ): Promise<void> {
    const requestBody = this.buildEventRequestBody(thumbnail, additionalImages);
    const changedFields = {
      event: this.getEventChangedFields(requestBody.event),
    };
    if (Object.keys(changedFields.event).length > 0) {
      try {
        await firstValueFrom(
          this.eventsManagementService.updateEvent(
            Number(this.eventId),
            changedFields as any,
          ),
        );
        this.rememberSavedEvent(requestBody.event);
      } catch (error) {
        this.showSaveError(error, false);
        return;
      }
    }
    await this.finishSavingEvent(false);
  }

  private showSaveError(error: unknown, creating: boolean): void {
    const explanation = describeEventSaveError(error, creating);
    this.saveError.set(explanation);
    this.isSubmitting.set(false);
    this.snackbarService.openSnackBar(
      `${explanation.title} ${explanation.details.join(" ")}`,
      "Fechar",
      "error",
      12000,
    );
  }

  private rememberSavedEvent(event: EventInfoRequestBody["event"]): void {
    // Only the parent is confirmed here; child baselines are updated independently.
    this.eventInfo = {
      ...this.eventInfo,
      ...event,
      id: this.eventId!,
      address: { ...this.eventInfo.address, ...event.address_attributes },
      event_category: {
        ...this.eventInfo.event_category,
        id: event.event_category_id,
      },
    } as EventInfoResponseBody;
  }

  private async finishSavingEvent(created: boolean): Promise<void> {
    try {
      const batchFailures = await this.syncTicketBatchesForUpdate();
      const result = await this.spacesSync.sync(
        Number(this.eventId),
        this.spacesSignal(),
        this.originalSpaces,
      );
      this.spacesSignal.set(result.spaces);
      this.originalSpaces = result.persisted;
      const resolvedCombos = this.resolveComboComponents(this.combosSignal());
      this.combosSignal.set(resolvedCombos);
      const comboResult = await this.combosSync.sync(
        Number(this.eventId),
        resolvedCombos,
        this.originalCombos,
      );
      this.combosSignal.set(comboResult.combos);
      this.originalCombos = comboResult.persisted;
      const failures = [
        ...batchFailures,
        ...result.failures,
        ...comboResult.failures,
      ];
      if (failures.length) {
        const details = failures.slice(0, 3).join("; ");
        const more =
          failures.length > 3
            ? ` e mais ${failures.length - 3} pendência(s)`
            : "";
        this.snackbarService.openSnackBar(
          `O evento foi salvo, mas há pendências: ${details}${more}. Os itens salvos foram mantidos. Revise e salve novamente.`,
          "Fechar",
          "warning",
          12000,
        );
        return;
      }
      const duplicateNote = result.duplicates
        ? ` ${result.duplicates} cópia(s) idêntica(s) ignorada(s).`
        : "";
      this.snackbarService.openSnackBar(
        (created
          ? "Evento criado com sucesso! Acompanhe a aprovação na página de solicitações."
          : "Evento atualizado com sucesso!") + duplicateNote,
        "Fechar",
        "success",
      );
      await this.router.navigate([this.eventDestinationRoute(this.eventId)]);
    } catch {
      this.snackbarService.openSnackBar(
        "Não foi possível concluir o salvamento. Os itens já confirmados foram mantidos; revise e tente novamente.",
        "Fechar",
        "warning",
        10000,
      );
    } finally {
      this.isSubmitting.set(false);
    }
  }

  private async deleteRemovedTicketBatches(
    batches: EventTicketBatch[],
    originals: EventTicketBatch[],
    failures: string[],
  ): Promise<void> {
    const currentBatchIds = new Set(
      batches.map((batch) => batch.id).filter((id): id is number => !!id),
    );

    for (const original of [...originals]) {
      if (!original.id || currentBatchIds.has(original.id)) continue;

      try {
        await firstValueFrom(
          this.ticketsManagementService.deleteBatch(original.id),
        );
        const removedIndex = originals.findIndex(
          (batch) => batch.id === original.id,
        );
        if (removedIndex >= 0) originals.splice(removedIndex, 1);
        this.originalTicketBatches = originals;
      } catch {
        failures.push(`Excluir lote #${original.id}`);
      }
    }

    for (const batch of batches) {
      if (!batch.id) continue;
      const original = originals.find((item) => item.id === batch.id);
      if (!original) continue;

      const currentTicketIds = new Set(
        batch.tickets
          .map((ticket) => ticket.id)
          .filter((id): id is number => !!id),
      );

      const removedTickets = [...original.tickets]
        .filter(
          (
            ticket,
          ): ticket is EventTicketBatch["tickets"][number] & { id: number } =>
            typeof ticket.id === "number" && !currentTicketIds.has(ticket.id),
        )
        .sort(
          (left, right) =>
            Number(!!right.half_price) - Number(!!left.half_price),
        );

      for (const ticket of removedTickets) {
        try {
          await firstValueFrom(
            this.ticketsManagementService.deleteTicketInBatch(
              batch.id,
              ticket.id,
            ),
          );
          original.tickets = original.tickets.filter(
            (item) => item.id !== ticket.id,
          );
          this.originalTicketBatches = originals;
        } catch {
          failures.push(
            `Excluir ingresso "${ticket.title}" no lote #${batch.id}`,
          );
        }
      }
    }
  }

  private async syncTicketBatchesForUpdate(): Promise<string[]> {
    const failures: string[] = [];
    const batches = this.ticketBatchesSignal().map((b) => ({
      ...b,
      tickets: b.tickets.map((t) => ({ ...t })),
    }));
    const originals = this.originalTicketBatches.map((b) => ({
      ...b,
      tickets: b.tickets.map((t) => ({ ...t })),
    }));
    await this.deleteRemovedTicketBatches(batches, originals, failures);
    const remember = (batch: EventTicketBatch) => {
      const index = originals.findIndex((b) => b.id === batch.id);
      const copy = { ...batch, tickets: batch.tickets.map((t) => ({ ...t })) };
      if (index < 0) originals.push(copy);
      else originals[index] = copy;
      this.originalTicketBatches = originals;
      this.ticketBatchesSignal.set([...batches]);
    };
    for (let index = 0; index < batches.length; index++) {
      let batch = batches[index];
      if (!batch.id) {
        try {
          const saved = await firstValueFrom(
            this.ticketsManagementService.createTicketBatch(
              Number(this.eventId),
              batch,
            ),
          );
          if (!saved.id) throw new Error("Lote sem confirmação");
          batch = batches[index] = {
            ...batch,
            ...saved,
          } as unknown as EventTicketBatch;
          remember(batch);
        } catch {
          failures.push("Criar lote de ingressos");
        }
        continue;
      }
      let original = originals.find((b) => b.id === batch.id);
      const datesOrFeesChanged =
        original &&
        ((original.sale_start ? Date.parse(original.sale_start) : null) !==
          (batch.sale_start ? Date.parse(batch.sale_start) : null) ||
          (original.sale_end ? Date.parse(original.sale_end) : null) !==
            (batch.sale_end ? Date.parse(batch.sale_end) : null) ||
          (original.transition_mode ?? "date_or_sold_out") !==
            (batch.transition_mode ?? "date_or_sold_out") ||
          !!original.absorbs_platform_fee !== !!batch.absorbs_platform_fee ||
          !!original.absorbs_processing_fee !==
            !!batch.absorbs_processing_fee ||
          !!original.sold_at_pdv !== !!batch.sold_at_pdv ||
          !!original.has_half_price !== !!batch.has_half_price);
      if (datesOrFeesChanged) {
        const update = {
          sale_start: batch.sale_start,
          sale_end: batch.sale_end,
          transition_mode: batch.transition_mode ?? "date_or_sold_out",
          absorbs_platform_fee: !!batch.absorbs_platform_fee,
          absorbs_processing_fee: !!batch.absorbs_processing_fee,
          sold_at_pdv: !!batch.sold_at_pdv,
          has_half_price: !!batch.has_half_price,
        };
        try {
          const saved = await firstValueFrom(
            this.ticketsManagementService.updateBatch(batch.id, update),
          );
          // Saving batch dates must not discard still-unsaved ticket edits or
          // mark them as already persisted. Only metadata was confirmed here.
          if (saved) {
            const { tickets: savedTickets, ...metadata } = saved;
            Object.assign(batch, metadata);
            for (const ticket of savedTickets ?? []) {
              if (!ticket.half_price || batch.tickets.some((draft) => draft.id === ticket.id)) continue;
              const draft = batch.tickets.find((item) => !item.id && item.half_price &&
                (item.parent_ticket_id === ticket.parent_ticket_id || item.title === ticket.title));
              if (draft) Object.assign(draft, { id: ticket.id, batch_id: ticket.batch_id });
              else batch.tickets.push({ ...ticket, price: Number(ticket.price) } as EventTicketBatch["tickets"][number]);
            }
          }
          remember({ ...batch, tickets: original!.tickets });
          original = originals.find((b) => b.id === batch.id);
        } catch {
          failures.push(`Atualizar lote #${batch.id}`);
        }
      }
      for (
        let ticketIndex = 0;
        ticketIndex < batch.tickets.length;
        ticketIndex++
      ) {
        const ticket = batch.tickets[ticketIndex];
        if (!ticket.id && ticket.half_price) continue;
        const previous = original?.tickets.find((t) => t.id === ticket.id);
        if (ticket.id && JSON.stringify(previous) === JSON.stringify(ticket))
          continue;
        try {
          const response = await firstValueFrom(
            ticket.id
              ? this.ticketsManagementService.updateTicketInBatch(
                  batch.id,
                  ticket.id,
                  ticket,
                )
              : this.ticketsManagementService.createTicketInBatch(
                  batch.id,
                  ticket,
                ),
          );
          if (!response.id) throw new Error("Ingresso sem confirmação");
          batch.tickets[ticketIndex] = { ...ticket, ...response };
          const savedTicket = batch.tickets[ticketIndex];
          const halfTicket = response.half_price_ticket;
          if (halfTicket) {
            const halfIndex = batch.tickets.findIndex(
              (item) =>
                item.half_price &&
                !item.id &&
                (item.parent_ticket_id === savedTicket.id ||
                  item.title === halfTicket.title),
            );
            if (halfIndex >= 0) {
              batch.tickets[halfIndex] = {
                ...batch.tickets[halfIndex],
                ...halfTicket,
                price: Number(halfTicket.price),
              };
            } else {
              batch.tickets.push({
                ...halfTicket,
                price: Number(halfTicket.price),
              });
            }
          }
          const confirmedTickets = (original?.tickets ?? []).map((item) => ({ ...item }));
          for (const confirmed of [savedTicket, ...(halfTicket ? [{ ...halfTicket, price: Number(halfTicket.price) }] : [])]) {
            const confirmedIndex = confirmedTickets.findIndex((item) => item.id === confirmed.id);
            if (confirmedIndex < 0) confirmedTickets.push(confirmed as EventTicketBatch["tickets"][number]);
            else confirmedTickets[confirmedIndex] = confirmed as EventTicketBatch["tickets"][number];
          }
          // Failed lot metadata and other ticket drafts remain pending for retry.
          remember({ ...(original ?? batch), tickets: confirmedTickets });
          original = originals.find((item) => item.id === batch.id);
        } catch {
          failures.push(
            `Salvar ingresso "${ticket.title}" no lote #${batch.id}`,
          );
        }
      }
    }
    return failures;
  }

  private resolveComboComponents(
    combos: EventComboTicket[],
  ): EventComboTicket[] {
    const tickets = this.ticketBatchesSignal().flatMap(
      (batch) => batch.tickets ?? [],
    );
    return combos.map((combo) => {
      const selected = (combo.component_tickets ?? []).map((component) => {
        const byId = component.id
          ? tickets.find((ticket) => Number(ticket.id) === Number(component.id))
          : undefined;
        const byTitle = tickets.find(
          (ticket) => ticket.title === component.title,
        );
        const match = byId ?? byTitle;
        return {
          id: Number(match?.id ?? component.id ?? 0),
          title: match?.title ?? component.title,
          quantity: Math.max(1, Number(component.quantity ?? 1)),
        };
      });
      return {
        ...combo,
        component_tickets: selected,
        component_ticket_ids: selected
          .map((item) => item.id)
          .filter((id) => id > 0),
      };
    });
  }

  private buildEventRequestBody(
    thumbnail: string,
    additionalImages: string[],
  ): EventInfoRequestBody {
    const basicInfo = this.formGroups.basicInfo.getRawValue();
    const eventDates = this.formGroups.eventDates.getRawValue();
    const address = this.formGroups.address.getRawValue();
    const summary = this.formGroups.eventSummary.getRawValue();
    const eventType = this.formGroups.eventType.getRawValue();

    return {
      event: {
        online: false,
        platform_url: "",
        title: basicInfo.title ?? "",
        description: basicInfo.description ?? "",
        thumbnail: thumbnail ?? "",
        additional_images: additionalImages ?? [],
        start_date: eventDates.startDateTime?.toISOString() ?? "",
        end_date: eventDates.endDateTime?.toISOString() ?? "",
        accepted_terms: !!summary.acceptedTerms,
        is_private: !!summary.isPrivate,
        event_type: eventType.isEventPaid ? "paid" : "free",
        status: summary.status ?? "draft",
        event_category_id: basicInfo.eventCategoryId!,
        address_attributes: {
          location_name: address.location_name ?? "",
          city_name: address.city_name ?? "",
          state: address.state ?? "",
          country: address.country ?? "BR",
          district: address.district ?? "",
          street: address.street ?? "",
          postal_code: address.postal_code ?? "",
          number: address.number ?? "",
          additional_information: address.additional_information ?? "",
          maps_url: address.maps_url ?? "",
        },
      },
    };
  }

  getEventChangedFields(newFormValues: any) {
    const oldEventInfoForm: any = this.eventInfo;
    oldEventInfoForm.address_attributes = oldEventInfoForm.address;

    const changedFields: Record<string, any> = {};

    const isPlainObject = (v: unknown) =>
      !!v && typeof v === "object" && !Array.isArray(v);

    const toComparableISO = (v: unknown): string | null => {
      if (!v) return null;

      // Date
      if (v instanceof Date) {
        if (Number.isNaN(v.getTime())) return null;
        const dt = DateTime.fromJSDate(v);
        return dt.isValid ? dt.toUTC().toISO() : null;
      }

      // string (ISO)
      if (typeof v === "string") {
        const dt = DateTime.fromISO(v);
        return dt.isValid ? dt.toUTC().toISO() : null;
      }

      return null;
    };

    const areEqualForPath = (path: string, a: unknown, b: unknown): boolean => {
      // trata datas do evento (backend string vs form Date/string)
      if (path === "start_date" || path === "end_date") {
        const aISO = toComparableISO(a);
        const bISO = toComparableISO(b);

        // se ambos viram datas válidas, compara por instante
        if (aISO && bISO) return aISO === bISO;

        // se não conseguiu normalizar, cai pro comparador padrão
        return a === b;
      }

      // padrão
      return a === b;
    };

    const setChangedField = (path: string, value: any) => {
      const parts = path.split(".");
      let current = changedFields;

      parts.forEach((part, index) => {
        if (index === parts.length - 1) {
          current[part] = value;
        } else {
          if (!current[part]) current[part] = {};
          current = current[part];
        }
      });
    };

    const compareValues = (
      initialValue: any,
      currentValue: any,
      path: string = "",
    ) => {
      // objetos aninhados
      if (isPlainObject(initialValue) && isPlainObject(currentValue)) {
        Object.keys(currentValue).forEach((key) => {
          const newPath = path ? `${path}.${key}` : key;
          compareValues(initialValue[key], currentValue[key], newPath);
        });
        return;
      }

      // diferença (com regra especial pra datas)
      if (!areEqualForPath(path, initialValue, currentValue)) {
        if (path) setChangedField(path, currentValue);
      }
    };

    compareValues(oldEventInfoForm, newFormValues);

    return changedFields;
  }

  private hydrateMediaFromFormIfNeeded(): void {
    const bannerFromForm =
      this.formGroups.basicInfo.controls.media.controls.uploadBanner.value;
    const mapFromForm =
      this.formGroups.basicInfo.controls.media.controls.mapaEvento.value;

    // Banner
    if (bannerFromForm instanceof File) {
      if (!this.localBannerFile) this.localBannerFile = bannerFromForm;
      if (!this.bannerBlobPath) {
        this.bannerBlobPath = `banner-${this.currentUser.id}/${bannerFromForm.name}`;
      }
    }

    // Mapa
    if (mapFromForm instanceof File) {
      if (!this.localMapFile) this.localMapFile = mapFromForm;
      if (!this.mapBlobPath) {
        this.mapBlobPath = `map-${this.currentUser.id}/${mapFromForm.name}`;
      }
    }

    console.log("[debug banner]", {
      bannerFromForm,
      isBannerFile: bannerFromForm instanceof File,
      localBannerFile: this.localBannerFile,
      bannerBlobPath: this.bannerBlobPath,
      eventInfoThumbnail: this.eventInfo.thumbnail,
    });
  }

  private syncInitialMediaPreviewsFromEventInfo(): void {
    // banner
    const hasThumbnail =
      !!this.eventInfo.thumbnail &&
      this.eventInfo.thumbnail !== "FALTA IMPLEMENTAR";

    this.initialBannerPreview = hasThumbnail
      ? `${environment.BLOB_URL}/${this.eventInfo.thumbnail}`
      : null;

    // mapa (pega o primeiro, como seu card antigo)
    const mapPath = this.eventInfo.additional_images?.[0];
    const hasMap = !!mapPath && mapPath !== "FALTA IMPLEMENTAR";

    this.initialMapPreview = hasMap
      ? `${environment.BLOB_URL}/${mapPath}`
      : null;

    // ✅ IMPORTANT: se já tem banner no backend, não faz sentido exigir uploadBanner
    const bannerCtrl =
      this.formGroups.basicInfo.controls.media.controls.uploadBanner;

    if (this.initialBannerPreview) {
      bannerCtrl.clearValidators();
      bannerCtrl.updateValueAndValidity({ emitEvent: false });
    } else {
      // create (ou evento sem thumbnail) => mantém required
      bannerCtrl.setValidators([Validators.required]);
      bannerCtrl.updateValueAndValidity({ emitEvent: false });
    }
  }
}
